import type { Portfolio } from './types'

const portfolioKey = 'portfolio'
const portfolioUpdatedAtKey = 'portfolio-updated-at'
const portfolioUsedAtKey = 'portfolio-used-at'
const portfolioIsWritableKey = 'portfolio-is-writable'

/** portfolio ごとの最初の未同期 write 時刻を保存する key prefix。 */
const portfolioDbDirtySinceKeyPrefix = 'portfolio-db-dirty-since:'

const progressesKey = 'progresses'
const exchangeApiProgressesKey = 'exchange-api-progresses'
const calculationProgressKey = 'calculation-progress'

export const getPortfolioFromLocalStorage = (): Portfolio | undefined => {
  const portfolio = localStorage.getItem(portfolioKey) ?? undefined
  if (portfolio === undefined) {
    return undefined
  }
  return JSON.parse(portfolio)
}

export const setPortfolioToLocalStorage = (
  portfolio: Portfolio | undefined,
) => {
  if (portfolio === undefined) {
    localStorage.removeItem(portfolioKey)
    return
  }
  const portfolioJson = JSON.stringify(portfolio)
  localStorage.setItem(portfolioKey, portfolioJson)
}

export const getPortfolioUpdatedAtFromLocalStorage = (): number | undefined => {
  const portfolioUpdatedAt =
    localStorage.getItem(portfolioUpdatedAtKey) ?? undefined
  if (portfolioUpdatedAt === undefined) {
    return undefined
  }
  return Number.parseInt(portfolioUpdatedAt)
}

export const setPortfolioUpdatedAtToLocalStorage = (
  portfolioUpdatedAt: number | undefined,
) => {
  if (portfolioUpdatedAt === undefined) {
    localStorage.removeItem(portfolioUpdatedAtKey)
    return
  }
  localStorage.setItem(portfolioUpdatedAtKey, portfolioUpdatedAt.toString())
}

/**
 * portfolio ごとの dirty timestamp 用 localStorage key を生成する。
 * @param params portfolio ID を受け取る。
 * @returns portfolio ID を含む localStorage key を返す。
 */
const createPortfolioDbDirtySinceKey = ({
  portfolioId,
}: {
  readonly portfolioId: string
}): string => `${portfolioDbDirtySinceKeyPrefix}${portfolioId}`

/**
 * portfolio が最初に未同期になった時刻を取得する。
 * @param params 取得対象の portfolio ID を受け取る。
 * @returns dirty timestamp。未設定の場合は undefined を返す。
 */
export const getPortfolioDbDirtySinceFromLocalStorage = ({
  portfolioId,
}: {
  readonly portfolioId: string
}): number | undefined => {
  const dirtySince =
    localStorage.getItem(createPortfolioDbDirtySinceKey({ portfolioId })) ??
    undefined
  if (dirtySince === undefined) {
    return undefined
  }
  return Number.parseInt(dirtySince)
}

/**
 * portfolio が最初に未同期になった時刻を保存または削除する。
 * @param params portfolio ID と dirty timestamp を受け取る。
 */
export const setPortfolioDbDirtySinceToLocalStorage = ({
  portfolioId,
  dirtySince,
}: {
  readonly portfolioId: string
  readonly dirtySince: number | undefined
}): void => {
  const key = createPortfolioDbDirtySinceKey({ portfolioId })
  if (dirtySince === undefined) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, dirtySince.toString())
}

/**
 * 成功した DB write を記録し、最初の未同期時刻を保持する。
 * @param params portfolio ID と write 成功時刻を受け取る。
 */
export const markPortfolioDbDirtyInLocalStorage = ({
  portfolioId,
  updatedAt,
}: {
  readonly portfolioId: string
  readonly updatedAt: number
}): void => {
  setPortfolioUpdatedAtToLocalStorage(updatedAt)
  const dirtySince = getPortfolioDbDirtySinceFromLocalStorage({ portfolioId })
  if (dirtySince === undefined) {
    setPortfolioDbDirtySinceToLocalStorage({
      portfolioId,
      dirtySince: updatedAt,
    })
  }
}

/**
 * upload 済み snapshot より新しい write がなければ dirty 状態を解除する。
 * @param params portfolio ID と upload 済み snapshot の timestamp を受け取る。
 */
export const markPortfolioDbSyncedInLocalStorage = ({
  portfolioId,
  syncedUpdatedAt,
}: {
  readonly portfolioId: string
  readonly syncedUpdatedAt: number
}): void => {
  const localUpdatedAt = getPortfolioUpdatedAtFromLocalStorage()
  if (localUpdatedAt !== undefined && localUpdatedAt <= syncedUpdatedAt) {
    setPortfolioDbDirtySinceToLocalStorage({
      portfolioId,
      dirtySince: undefined,
    })
  }
}

export const getPortfolioUsedAtFromLocalStorage = (): number | undefined => {
  const portfolioUsedAt = localStorage.getItem(portfolioUsedAtKey) ?? undefined
  if (portfolioUsedAt === undefined) {
    return undefined
  }
  return Number.parseInt(portfolioUsedAt)
}

export const setPortfolioUsedAtToLocalStorage = (
  portfolioUsedAt: number | undefined,
  onlyIfNewer = false,
) => {
  if (onlyIfNewer) {
    const currentPortfolioUsedAt = getPortfolioUsedAtFromLocalStorage() ?? 0
    if (
      portfolioUsedAt === undefined ||
      portfolioUsedAt <= currentPortfolioUsedAt
    ) {
      return
    }
    localStorage.setItem(portfolioUsedAtKey, portfolioUsedAt.toString())
  } else {
    if (portfolioUsedAt === undefined) {
      localStorage.removeItem(portfolioUsedAtKey)
      return
    }
    localStorage.setItem(portfolioUsedAtKey, portfolioUsedAt.toString())
  }
}

export const getPortfolioIsWritableFromLocalStorage = (): boolean => {
  const portfolioIsWritable =
    localStorage.getItem(portfolioIsWritableKey) ?? 'false'
  return portfolioIsWritable === 'true'
}

export const setPortfolioIsWritableToLocalStorage = (
  portfolioIsWritable: boolean | undefined,
) => {
  if (portfolioIsWritable === undefined) {
    localStorage.removeItem(portfolioIsWritableKey)
    return
  }
  localStorage.setItem(portfolioIsWritableKey, portfolioIsWritable.toString())
}

/** 現在の portfolio とその portfolio 固有の dirty 状態を削除する。 */
export const clearPortfolioDataFromLocalStorage = (): void => {
  const portfolio = getPortfolioFromLocalStorage()
  if (portfolio !== undefined) {
    setPortfolioDbDirtySinceToLocalStorage({
      portfolioId: portfolio.uuid,
      dirtySince: undefined,
    })
  }
  localStorage.removeItem(portfolioKey)
  localStorage.removeItem(portfolioUpdatedAtKey)
  localStorage.removeItem(portfolioUsedAtKey)
  localStorage.removeItem(portfolioIsWritableKey)
}

export const existProgressInLocalStorage = (): boolean => {
  const progresses = localStorage.getItem(progressesKey)
  if (progresses !== null && progresses !== '' && progresses !== '[]') {
    return true
  }
  const exchangeApiProgresses = localStorage.getItem(exchangeApiProgressesKey)
  if (
    exchangeApiProgresses !== null &&
    exchangeApiProgresses !== '' &&
    exchangeApiProgresses !== '{}'
  ) {
    return true
  }
  const calculationProgress = localStorage.getItem(calculationProgressKey)
  if (
    calculationProgress !== null &&
    calculationProgress !== '' &&
    calculationProgress !== 'undefined' &&
    calculationProgress !== '{}'
  ) {
    return true
  }
  return false
}
